import React, { useState, useEffect, useCallback } from "react";
import {
	Spin,
	message,
	Tabs,
	Table,
	Button,
	Card,
	Statistic,
	Tag,
	Modal,
	InputNumber,
	Select,
} from "antd";
import { LoadingOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import {
	getBulkPurchases,
	getBulkPurchaseCodes,
	revokeBulkSubscriptionCode,
	getBulkSubscriptionStats,
	createBulkCheckoutSession,
	getStripeProducts,
} from "../../utils/api";

const { TabPane } = Tabs;
const { Option } = Select;

// Custom styles for dark theme table
const darkTableStyle = `
  .dark-theme-table .ant-table {
    background: transparent !important;
    color: white !important;
  }
  .dark-theme-table .ant-table-thead > tr > th {
    background: rgba(255, 255, 255, 0.1) !important;
    color: white !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
  }
  .dark-theme-table .ant-table-tbody > tr > td {
    background: transparent !important;
    color: rgba(255, 255, 255, 0.8) !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  .dark-theme-table .ant-table-tbody > tr:hover > td {
    background: rgba(255, 255, 255, 0.05) !important;
  }
  .dark-theme-table .ant-pagination-item a {
    color: white !important;
  }
  .dark-theme-table .ant-pagination-item-active {
    border-color: #06b6d4 !important;
    background: rgba(6, 182, 212, 0.2) !important;
  }
  .dark-theme-table .ant-pagination-prev .ant-pagination-item-link,
  .dark-theme-table .ant-pagination-next .ant-pagination-item-link {
    color: white !important;
    background: transparent !important;
  }
`;

const ManagerSubscription = () => {
	const [loading, setLoading] = useState(true);
	const [stats, setStats] = useState(null);
	const [purchases, setPurchases] = useState([]);
	const [products, setProducts] = useState([]);

	// Purchase State
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [quantity, setQuantity] = useState(5);
	const [purchasing, setPurchasing] = useState(false);

	const fetchInitialData = useCallback(async () => {
		setLoading(true);
		try {
			const statsRes = await getBulkSubscriptionStats();
			if (statsRes?.data?.statusCode === 200) {
				setStats(statsRes.data.data);
			}

			const purchasesRes = await getBulkPurchases({ limit: 100 });
			if (purchasesRes?.data?.statusCode === 200) {
				setPurchases(purchasesRes.data.data.purchases);
			}

			const productsRes = await getStripeProducts();
			const productsData = productsRes?.data?.data || productsRes?.data || [];
			if (Array.isArray(productsData)) {
				setProducts(productsData);
				if (productsData.length > 0) {
					// Find first product with valid price
					const validProduct = productsData.find(
						(p) => p.prices && p.prices.length > 0,
					);
					if (validProduct) {
						// Prefer yearly if available
						const price =
							validProduct.prices.find((p) => p.interval === "year") ||
							validProduct.prices[0];
						setSelectedProduct(price.stripePriceId);
					}
				}
			}
		} catch (error) {
			console.error("Error fetching manager subscription data:", error);
			message.error("Failed to load subscription data");
		}
		setLoading(false);
	}, []);

	useEffect(() => {
		fetchInitialData();
	}, [fetchInitialData]);

	const handlePurchase = async () => {
		if (!selectedProduct) {
			message.error("Please select a plan");
			return;
		}
		if (quantity < 1) {
			message.error("Quantity must be at least 1");
			return;
		}

		setPurchasing(true);
		try {
			const res = await createBulkCheckoutSession({
				priceId: selectedProduct,
				quantity: quantity,
			});

			if (res?.data?.statusCode === 200 && res.data.data.url) {
				window.location.href = res.data.data.url;
			} else {
				message.error(res?.data?.message || "Failed to initiate purchase");
			}
		} catch (error) {
			console.error("Purchase error:", error);
			message.error("An error occurred during purchase");
		}
		setPurchasing(false);
	};

	if (loading) {
		return (
			<div className="flex justify-center items-center py-20">
				<Spin
					indicator={
						<LoadingOutlined style={{ fontSize: 48, color: "white" }} spin />
					}
				/>
			</div>
		);
	}

	return (
		<div className="max-w-6xl mx-auto px-4 pb-20">
			{/* Stats Cards */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
				<Card className="bg-white/10 border-white/20">
					<Statistic
						title={<span className="text-gray-300">Total Codes Purchased</span>}
						value={stats?.codes?.total || 0}
						valueStyle={{ color: "#fff" }}
					/>
				</Card>
				<Card className="bg-white/10 border-white/20">
					<Statistic
						title={<span className="text-gray-300">Available Codes</span>}
						value={stats?.codes?.available || 0}
						valueStyle={{ color: "#4ade80" }}
					/>
				</Card>
				<Card className="bg-white/10 border-white/20">
					<Statistic
						title={<span className="text-gray-300">Redeemed Codes</span>}
						value={stats?.codes?.redeemed || 0}
						valueStyle={{ color: "#60a5fa" }}
					/>
				</Card>
				<Card className="bg-white/10 border-white/20">
					<Statistic
						title={<span className="text-gray-300">Total Spent</span>}
						value={stats?.purchases?.totalSpent || 0}
						prefix="$"
						precision={2}
						valueStyle={{ color: "#fbbf24" }}
					/>
				</Card>
			</div>

			<div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
				<Tabs defaultActiveKey="purchase" centered>
					<TabPane
						tab={<span className="text-white">Purchase Codes</span>}
						key="purchase"
					>
						<div className="max-w-2xl mx-auto py-8">
							<h2 className="text-2xl font-bold text-white mb-6 text-center">
								Buy Bulk Subscriptions
							</h2>

							<div className="space-y-6">
								<div>
									<label className="block text-gray-300 mb-2">
										Select Plan
									</label>
									<Select
										className="w-full"
										placeholder="Select a plan"
										onChange={setSelectedProduct}
										value={selectedProduct}
										size="large"
									>
										{products.map((product) =>
											product.prices?.map((price) => (
												<Option
													key={price.stripePriceId}
													value={price.stripePriceId}
												>
													{product.name} - $
													{(price.unitAmount / 100).toFixed(2)} /{" "}
													{price.interval}
												</Option>
											)),
										)}
									</Select>
								</div>

								<div>
									<label className="block text-gray-300 mb-2">Quantity</label>
									<InputNumber
										min={1}
										max={100}
										value={quantity}
										onChange={setQuantity}
										className="w-full"
										size="large"
									/>
									<p className="text-gray-400 text-sm mt-1">
										Minimum 1 code, Maximum 100 codes per transaction.
									</p>
								</div>

								<div className="pt-4">
									<Button
										type="primary"
										size="large"
										block
										onClick={handlePurchase}
										loading={purchasing}
										className="h-12 bg-gradient-to-r from-cyan-500 to-blue-600 border-none hover:from-cyan-600 hover:to-blue-700"
									>
										Proceed to Payment
									</Button>
								</div>
							</div>
						</div>
					</TabPane>

					<TabPane
						tab={<span className="text-white">Purchase History</span>}
						key="history"
					>
						<style>{darkTableStyle}</style>
						<Table
							dataSource={purchases}
							rowKey="id"
							className="ant-table-dark dark-theme-table"
							pagination={{ pageSize: 5 }}
							columns={[
								{
									title: "Date",
									dataIndex: "createdAt",
									key: "createdAt",
									render: (date) => (
										<span className="text-gray-300">
											{new Date(date).toLocaleDateString()}
										</span>
									),
								},
								{
									title: "Product",
									dataIndex: "productName",
									key: "productName",
									render: (text) => (
										<span className="text-white font-medium">{text}</span>
									),
								},
								{
									title: "Quantity",
									dataIndex: "quantity",
									key: "quantity",
									render: (qty) => <span className="text-gray-300">{qty}</span>,
								},
								{
									title: "Total",
									dataIndex: "totalAmount",
									key: "totalAmount",
									render: (amount) => (
										<span className="text-gray-300">${amount.toFixed(2)}</span>
									),
								},
								{
									title: "Status",
									dataIndex: "status",
									key: "status",
									render: (status) => (
										<Tag
											color={
												status === "completed"
													? "green"
													: status === "pending"
														? "orange"
														: "red"
											}
										>
											{status.toUpperCase()}
										</Tag>
									),
								},
								{
									title: "Actions",
									key: "actions",
									render: (_, record) => (
										<Button
											type="link"
											onClick={() => {
												Modal.info({
													title: "Subscription Codes",
													width: 800,
													content: <CodesList purchaseId={record.id} />,
													icon: null,
													okText: "Close",
													maskClosable: true,
												});
											}}
										>
											View Codes
										</Button>
									),
								},
							]}
						/>
					</TabPane>
				</Tabs>
			</div>
		</div>
	);
};

const CodesList = ({ purchaseId }) => {
	const [codes, setCodes] = useState([]);
	const [loading, setLoading] = useState(true);

	const fetchCodes = useCallback(async () => {
		setLoading(true);
		const res = await getBulkPurchaseCodes(purchaseId, { limit: 100 });
		if (res?.data?.statusCode === 200) {
			setCodes(res.data.data.codes);
		}
		setLoading(false);
	}, [purchaseId]);

	useEffect(() => {
		fetchCodes();
	}, [fetchCodes]);

	const handleRevoke = async (codeId) => {
		Modal.confirm({
			title: "Revoke Code",
			icon: <ExclamationCircleOutlined />,
			content:
				"Are you sure you want to revoke this code? It will no longer be usable.",
			onOk: async () => {
				try {
					const res = await revokeBulkSubscriptionCode(
						codeId,
						"Revoked by manager",
					);
					if (res?.data?.statusCode === 200) {
						message.success("Code revoked successfully");
						fetchCodes();
					} else {
						message.error(res?.data?.message || "Failed to revoke code");
					}
				} catch (error) {
					message.error("Error revoking code");
				}
			},
		});
	};

	return (
		<div className="py-4">
			<Table
				loading={loading}
				dataSource={codes}
				rowKey="id"
				pagination={{ pageSize: 5 }}
				size="small"
				columns={[
					{
						title: "Code",
						dataIndex: "code",
						key: "code",
						render: (code) => (
							<span className="font-mono font-bold select-all">{code}</span>
						),
					},
					{
						title: "Status",
						dataIndex: "status",
						key: "status",
						render: (status) => {
							let color = "green";
							if (status === "redeemed") color = "blue";
							if (status === "revoked") color = "red";
							if (status === "expired") color = "gray";
							return <Tag color={color}>{status.toUpperCase()}</Tag>;
						},
					},
					{
						title: "Redeemed By",
						dataIndex: "redeemedBy",
						key: "redeemedBy",
						render: (user) =>
							user ? (
								<span>
									{user.name} ({user.email})
								</span>
							) : (
								"-"
							),
					},
					{
						title: "Action",
						key: "action",
						render: (_, record) =>
							record.status === "available" ? (
								<Button
									size="small"
									danger
									onClick={() => handleRevoke(record.id)}
								>
									Revoke
								</Button>
							) : (
								"-"
							),
					},
				]}
			/>
		</div>
	);
};

export default ManagerSubscription;
